import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  OAuthContextProps,
  OAuthCredentialValue,
  OAuthProviderProps,
} from './OAuth.types';
import jwtDecode from 'jwt-decode';
import OAuth from './OAuth.class';
import { notification } from 'antd';
import { useHistory } from 'react-router-dom';

type CredentialTokens = { access_token?: string };

const OAuthContext = createContext({} as OAuthContextProps);

export const useOAuth = (credentialKey: string) =>
  useContext(OAuthContext).credentials[credentialKey];

export default function OAuthProvider(props: OAuthProviderProps) {
  const { credentials } = props;

  const history = useHistory();

  const [credentialKeyLoading, setCredentialKeyLoading] = useState<string>();
  const [credentialsValue, setCredentialsValue] = useState<
    Record<string, OAuthCredentialValue>
  >({});

  const clearSearchParams = useCallback(() => {
    const search = new URLSearchParams(history.location.search);
    search.delete('credential_key');
    search.delete('response_type');
    search.delete('client_id');
    search.delete('redirect_uri');
    search.delete('state');
    search.delete('code_challenge');
    search.delete('code_challenge_method');

    history.replace(`${history.location.pathname}?${search.toString()}`);
  }, [history.location.search]);

  const retrieveSearchCode = useCallback(async () => {
    try {
      const search = new URLSearchParams(history.location.search);
      const params = Object.fromEntries(search);

      if (!params.state || (params.state && !params.credential_key)) {
        clearSearchParams();
        return;
      }

      const credentialParams = credentials[params.credential_key];

      const OA = new OAuth({
        ...credentialParams,
        key: params.credential_key,
      });

      const authorizeCode = await OA.retriveCode();

      if (authorizeCode.code) {
        const tokens = await OA.retriveTokens(authorizeCode.code);

        setCredentialsValue((prev) => ({
          ...prev,
          [params.credential_key]: {
            ...prev[params.credential_key],
            isLoading: false,
            isAuthenticated: !!tokens.access_token,
          },
        }));
        setCredentialKeyLoading(undefined);
      } else {
        clearSearchParams();
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      notification.error({
        message: 'Erro ao acessar',
        description: error.message,
      });
      clearSearchParams();
    }
  }, [history]);

  useEffect(() => {
    retrieveSearchCode();
  }, [retrieveSearchCode]);

  const signIn = async (credentialKey: string) => {
    try {
      setCredentialKeyLoading(credentialKey);

      const OA = new OAuth({
        ...credentials[credentialKey],
        key: credentialKey,
      });

      const redirectUri = window.location.href;

      await OA.signInRedirect(redirectUri);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setCredentialKeyLoading(undefined);
      notification.destroy();
      notification.error(error.message);
    }
  };

  const isValidToken = (accessToken?: string) => {
    if (!accessToken) return false;

    const tokenDecoded = jwtDecode<{ exp: number }>(accessToken);

    const currentTimestamp = Math.floor(Date.now() / 1000);

    return (
      typeof tokenDecoded.exp === 'number' &&
      tokenDecoded.exp > currentTimestamp
    );
  };

  const revalidate = async (credentialKey: string) => {
    const credentialTokens =
      OAuth.getCredentialTokens<CredentialTokens>(credentialKey);

    const accessToken = credentialTokens?.access_token;

    if (!isValidToken(accessToken)) {
      await signIn(credentialKey);
      return false;
    }

    return true;
  };

  const credentialsMapper = useMemo(
    () =>
      Object.keys(credentials).map<OAuthCredentialValue & { key: string }>(
        (key) => {
          const credentialTokens =
            OAuth.getCredentialTokens<CredentialTokens>(key);

          return {
            key,
            config: credentials[key],
            isAuthenticated: !!credentialTokens?.access_token,
            isLoading: credentialKeyLoading === key,
            revalidate: () => revalidate(key),
            signIn: () => signIn(key),
          };
        },
      ),
    [credentialKeyLoading],
  );

  useEffect(() => {
    setCredentialsValue(
      credentialsMapper.reduce((acc, mapper) => {
        const { key, ...rest } = mapper;
        acc[key] = rest;
        return acc;
      }, {} as Record<string, OAuthCredentialValue>),
    );
  }, [credentialsMapper]);

  return (
    <OAuthContext.Provider value={{ credentials: credentialsValue }}>
      {props.children}
    </OAuthContext.Provider>
  );
}
